/**
 * Axios Client Configuration
 * Handles API communication with authentication and error handling
 */
import axios from 'axios';

/* -------------------------------------------------------------
   REQUIRED ENVIRONMENT VARIABLES
   These MUST be provided by Vite (.env.production)
------------------------------------------------------------- */

// API base URL (e.g. "https://isoboss.councilforstandards.org/api")
const BASE_URL = import.meta.env.VITE_API_URL;
if (!BASE_URL) {
  throw new Error("VITE_API_URL is missing — production build cannot run.");
}

// Static file base URL (e.g. "https://isoboss.councilforstandards.org/static")
export const STATIC_URL = import.meta.env.VITE_STATIC_URL;
if (!STATIC_URL) {
  throw new Error("VITE_STATIC_URL is missing — production build cannot run.");
}

/* -------------------------------------------------------------
   STATIC URL RESOLUTION
------------------------------------------------------------- */

/**
 * Resolve a backend-relative path (e.g. "/static/logos/1/logo.png")
 * into a fully-qualified URL pointing at the backend origin.
 *
 * Accepts:
 *   - root-relative paths ("/static/…")
 *   - absolute URLs ("https://…")
 *   - null/undefined
 */
export function resolveStaticUrl(url) {
  if (!url) return null;

  // Already absolute
  if (url.startsWith('http')) return url;

  // Backend root-relative path
  if (url.startsWith('/')) {
    const origin = STATIC_URL.replace(/\/static$/, ''); // strip trailing /static
    return `${origin}${url}`;
  }

  return url;
}

/* -------------------------------------------------------------
   AXIOS INSTANCE
------------------------------------------------------------- */

const axiosClient = axios.create({
  baseURL: BASE_URL,
  timeout: 600000, // 10 minutes for long-running document generation
  headers: {
    'Content-Type': 'application/json',
  },
});

/* -------------------------------------------------------------
   REQUEST INTERCEPTOR — Add Bearer token
------------------------------------------------------------- */

axiosClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

/* -------------------------------------------------------------
   RESPONSE INTERCEPTOR — Global error handling
------------------------------------------------------------- */

// FastAPI returns `detail` as a string for HTTPException, but as an array of
// Pydantic validation-error objects ({type, loc, msg, input, url}) on a 422.
// Rendering that array/object directly as a React child crashes the page
// (minified error #31), so always coerce it to a plain string first.
function toErrorMessage(detail) {
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d) => (d && typeof d === 'object' ? (d.msg || JSON.stringify(d)) : String(d))).join('; ');
  }
  if (detail && typeof detail === 'object') return detail.msg || JSON.stringify(detail);
  return null;
}

axiosClient.interceptors.response.use(
  (response) => response,

  (error) => {
    if (error.response) {
      const { status, data } = error.response;

      switch (status) {
        case 401:
          localStorage.removeItem('token');
          window.location.href = '/login';
          break;

        case 403:
          console.error('Access denied:', data.detail);
          break;

        case 404:
          console.error('Resource not found:', data.detail);
          break;

        case 500:
          console.error('Server error:', data.detail);
          break;

        default:
          console.error('API error:', data.detail || 'Unknown error');
      }

      error.userMessage = toErrorMessage(data.detail) || 'An error occurred';
    } else if (error.request) {
      error.userMessage = 'Network error. Please check your connection.';
    } else {
      error.userMessage = 'Request failed. Please try again.';
    }

    return Promise.reject(error);
  }
);

export default axiosClient;
